import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { ClientListForAddStudentResponse } from '../../../_models/clients/client-list-for-add-student-response.model';
import { ClientListing } from '../../../_models/clients/client-listing.model';
import { SearchStudent, StudentAdditionalDetails, StudentDetails, StudentPupilDetails } from '../../../_models/search-student/search-student.model';
import { StudentMergeResponse } from '../../../_models/search-student/student-merge-response.model';
import { StudentMaintenanceService } from '../../../_services/student-maintenance/student-maintenance.service';
import { ThemeService } from '../../../_helpers/theming-engine/theme.service';
import { AuthenticationService, ClientService, PlacementService, RoleService } from '../../../_services';
import { FeatureHelperService } from '../../../_helpers/feature-helper/feature-helper.service';
import { StudentService } from '../../../_services/student/student.service';
import { takeUntil } from 'rxjs/operators';
import { CustomValidators } from '../../../_helpers/custom-validators';
import { debounce } from 'lodash';
import { ErrorModalHelperService } from '../../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../../_helpers/success-modal-helper/success-modal-helper.service';
import { MatDialogRef } from '@angular/material/dialog';
import { LaunchVideoLibrary } from '../../../_models/digital-classroom/digital-classroom-launch-library.model';
import { DigitalClassroomAccess } from '../../../_models/digital-classroom/digital-classroom-access.model';
import { TutorReportsHelperService } from '../../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { PlacementInformation } from '../../../_models/maintenance/placement-maintenance.model';
import { Router } from '@angular/router';
import { ColumnDef } from '../../../shared/data-table/data-table.model';

@Component({
    selector: 'app-manual-add-modal',
    templateUrl: './manual-add-modal.component.html',
    styleUrls: ['./manual-add-modal.component.css'],
    standalone: false
})
export class ManualAddModalComponent implements OnInit, OnDestroy {

  @ViewChild('message') message: ElementRef;

  private unsubscribe: Subject<void> = new Subject<void>();

  public studentId: number;
  public studentDisplayName: string;

  public disableSave: boolean = true;
  public hasInsertPermissions: boolean = false;
  public hasUpdatePermissions: boolean = false;
  public showEditButton: boolean = false;

  public createLogins: boolean = false;

  public frmStudentMaintenance: UntypedFormGroup;

  public studentCreatedOn: Date;
  public studentUpdatedOn: Date;
  public studentQuickInfo: string;

  public clientList: Array<ClientListing> = new Array<ClientListing>();
  public filteredClientList: Array<ClientListing> = new Array<ClientListing>();
  public uploadSelectedClientId: number = 0;
  public showClientAccessError: boolean = false;

  public successfulUpload: boolean;
  public uploadResponseMainMessage: string = "";
  public uploadResponseMessages: Array<string> = new Array<string>();

  public modalHeader: string;
  public modalInitializing: boolean = true;

  public placementInformation: PlacementInformation[] = new Array<PlacementInformation>();
  public showLaunchVideoLibrary: boolean = false;
  private placement: PlacementInformation = new PlacementInformation;
  public bookingColumns: ColumnDef[] = [
    { field: 'subject', header: 'Subject' },
    { field: 'placementId', header: 'Booking ID' },
    { field: 'startDate', header: 'Start date', type: 'date' },
    { field: 'endDate', header: 'End date', type: 'date' },
    { field: 'actions', header: '', type: 'template', templateName: 'actions' },
  ];
  public hasLaunchVideoLibraryPermission: boolean = false;
  private userId: any;
  private clientId: string;


  constructor(
    private studentService: StudentService,
    private successModalHelper: SuccessModalHelperService,
    private clientService: ClientService,
    public themeService: ThemeService,
    private featureHelperService: FeatureHelperService,
    private studentMaintenanceService: StudentMaintenanceService,
    private formBuilder: UntypedFormBuilder,
    private errorModalHelper: ErrorModalHelperService,
    private dialogRef: MatDialogRef<ManualAddModalComponent>,
    private readonly placementService: PlacementService,
    private authenticationService: AuthenticationService,
    public tutorReportsHelperService: TutorReportsHelperService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    this.validateDateOfBirth = debounce(this.validateDateOfBirth, 1000)
  }

  ngOnInit() {
    this.initializeFormGroup();
    this.checkPermissions();
    this.populateClientDropdown();

    setTimeout(() => {

      this.disableSave = !this.hasUpdatePermissions;

      if (this.studentMaintenanceService.studentId != null && this.studentMaintenanceService.studentId != 0) {
        this.modalHeader = "View learner";
        this.studentId = this.studentMaintenanceService.studentId;
        this.showEditButton = true;
        this.populateForm();
      } else {
        this.modalHeader = "Add learner";
        this.modalInitializing = false;
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public enableEdit(): void {
    this.frmStudentMaintenance.enable();
  }

  public saveStudentMaintenance(): void {
    this.frmStudentMaintenance.updateValueAndValidity();
    this.frmStudentMaintenance.markAllAsTouched();
    if (this.frmStudentMaintenance.invalid) {
      return;
    }


    let student = new SearchStudent();
    student.studentPupilDetailsItem = new StudentPupilDetails();
    student.studentDetailItem = new StudentDetails();
    student.studentAdditionalDetailsItem = new StudentAdditionalDetails();

    student.studentPupilDetailsItem.upn = this.frmStudentMaintenance.get("upn").value;
    student.studentPupilDetailsItem.yearGroup = this.frmStudentMaintenance.get("yearGroup").value;
    student.studentPupilDetailsItem.mainSubject = this.frmStudentMaintenance.get("mainSubject").value;
    student.studentPupilDetailsItem.eligableForPupilPremium = this.frmStudentMaintenance.get("pupilPremium").value;
    student.studentPupilDetailsItem.sendPupil = this.frmStudentMaintenance.get("sendPupil").value;
    student.studentPupilDetailsItem.cin = this.frmStudentMaintenance.get("childrenInNeed").value;
    student.studentPupilDetailsItem.lac = this.frmStudentMaintenance.get("lookingAfterChildren").value;
    student.studentPupilDetailsItem.clientId = this.frmStudentMaintenance.get("client").value;
    student.studentDetailItem.firstName = this.frmStudentMaintenance.get("firstName").value;
    student.studentDetailItem.surname = this.frmStudentMaintenance.get("lastName").value;
    student.studentDetailItem.dateOfBirth = this.frmStudentMaintenance.get("dateOfBirth").value;
    student.studentDetailItem.gender = this.frmStudentMaintenance.get("gender").value;
    student.studentDetailItem.emailAddress = this.frmStudentMaintenance.get("emailAddress").value;
    student.studentDetailItem.age = this.frmStudentMaintenance.get("age").value;
    student.studentDetailItem.hiddenFromAllocation = this.frmStudentMaintenance.get("hiddenFromAllocation").value;
    student.studentAdditionalDetailsItem.learnerBackground = this.frmStudentMaintenance.get("learnerBackground").value;
    student.studentAdditionalDetailsItem.behaviourBackground = this.frmStudentMaintenance.get("behaviorBackground").value;
    student.studentAdditionalDetailsItem.healthAndSafety = this.frmStudentMaintenance.get("healthAndSafetyInfo").value;
    student.studentAdditionalDetailsItem.studentNotes = this.frmStudentMaintenance.get("additionalNotes").value;

    student.studentPupilDetailsItem.pupilDataSharingWithdrawn = this.frmStudentMaintenance.get("withdrawSharingData").value;

    if (student.studentDetailItem.dateOfBirth != null) {
      let dobHolder = new Date(student.studentDetailItem.dateOfBirth);
      dobHolder.setHours(12, 0, 0, 0);
      student.studentDetailItem.dateOfBirth = dobHolder.toUTCString();
    }

    if (this.studentId != null && this.studentId != 0) {
      //update
      student.studentId = this.studentId;
      this.studentService.updateStudent(student).subscribe((response: StudentMergeResponse) => {
        this.handleInsertUpdateResponse(response, "updated");
      }, error => {
        console.error(error);
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
      });
    } else {
      // insert
      this.studentService.insertStudent(student).subscribe((response: StudentMergeResponse) => {
        this.handleInsertUpdateResponse(response, "inserted");
      }, error => {
        console.error(error);
        this.errorModalHelper.show(error.message, error);
      });
    }

  }

  public validateDateOfBirth(): void {
    let controlValue: string = "";
    if (typeof this.frmStudentMaintenance.get("dateOfBirth").value != "string") {
      const controlDate = new Date(this.frmStudentMaintenance.get("dateOfBirth").value);
      const month = String(controlDate.getMonth() + 1).padStart(2, '0');
      const day = String(controlDate.getDate()).padStart(2, '0');
      const year = controlDate.getFullYear().toString();

      controlValue = day + "/" + month + "/" + year;
    } else {
      controlValue = this.frmStudentMaintenance.get("dateOfBirth").value.substring(0, this.frmStudentMaintenance.get("dateOfBirth").value.indexOf("T")).split('-').reverse().join('/');
    }

    let regPattern = /(^(((0[1-9]|1[0-9]|2[0-8])[\/](0[1-9]|1[012]))|((29|30|31)[\/](0[13578]|1[02]))|((29|30)[\/](0[4,6,9]|11)))[\/](19|[2-9][0-9])\d\d$)|(^29[\/]02[\/](19|[2-9][0-9])(00|04|08|12|16|20|24|28|32|36|40|44|48|52|56|60|64|68|72|76|80|84|88|92|96)$)/;

    const valid = regPattern.test(controlValue);

    if (!valid) {
      this.frmStudentMaintenance.get("dateOfBirth").setErrors({ invalidDateFormat: true });
    }
  }

  public close() {
    this.dialogRef.close();
  }

  private checkPermissions(): void {
    let featureList = new Array<string>();
    featureList.push(
      "StudentDataInsert",
      "StudentDataEdit",
      "LaunchVideoLibrary");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    this.hasLaunchVideoLibraryPermission = currentUserFeatures.find(f => f.feature == "LaunchVideoLibrary").enabled;

    if (currentUserFeatures.filter(f => f.feature === "StudentDataInsert")[0].enabled) {
      this.hasInsertPermissions = true;
    }
    if (currentUserFeatures.filter(f => f.feature === "StudentDataEdit")[0].enabled) {
      this.hasUpdatePermissions = true;
    }
  }

  private populateClientDropdown(): void {
    this.clientService.getClientListForAddStudent().subscribe((response: ClientListForAddStudentResponse) => {
      if (response.success) {
        if (this.studentMaintenanceService.clientIdFilter && this.studentMaintenanceService.clientIdFilter != 0) {
          this.clientList = response.clients.filter(client => client.clientId == this.studentMaintenanceService.clientIdFilter);
          this.frmStudentMaintenance.get("client").setValue(this.studentMaintenanceService.clientIdFilter);
        } else {
          this.clientList = response.clients;
        }
        this.filteredClientList = this.clientList;
      } else if (response.responseMessage.toLowerCase() == "no valid client roles assigned") {
        this.showClientAccessError = true;
      } else {
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", { isValidationError: false });
      }
    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  private initializeFormGroup(): void {
    if (this.frmStudentMaintenance == null) {

      this.frmStudentMaintenance = this.formBuilder.group({
        firstName: [null, Validators.compose([Validators.required])],
        lastName: [null, Validators.compose([Validators.required])],
        upn: [null, null],
        dateOfBirth: [null, Validators.compose([
          CustomValidators.DateFormatValidator("dateOfBirth", { invalidDateFormat: true })
        ])],
        yearGroup: [null, null],
        mainSubject: [null, null],
        client: [null, Validators.compose([Validators.required])],
        emailAddress: [null, null],
        pupilPremium: [false, null],
        sendPupil: [false, null],
        childrenInNeed: [false, null],
        lookingAfterChildren: [false, null],
        gender: ["6", null],
        age: [null, null],
        hiddenFromAllocation: [false, null],
        learnerBackground: [null, null],
        behaviorBackground: [null, null],
        healthAndSafetyInfo: [null, null],
        additionalNotes: [null, null],
        withdrawSharingData: [false, null],
      });

      this.frmStudentMaintenance.get("dateOfBirth").valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(selectedValue => {
        if (selectedValue != null) {
          let timeDiff = Math.abs(Date.now() - new Date(selectedValue).getTime());
          let age = Math.floor((timeDiff / (1000 * 3600 * 24)) / 365.25);
          this.frmStudentMaintenance.get("age").setValue(age);
        }
      })
    }
  }

  private populateForm(): void {
    this.userId = this.authenticationService.userSessionData.currentUser.id
    this.getPlacementInformation();
    this.studentService.selectStudent(this.studentId).subscribe((student: SearchStudent) => {
      if (student != null) {

        if (student.studentPupilDetailsItem != null) {
          this.frmStudentMaintenance.get("upn").setValue(student.studentPupilDetailsItem.upn);
          this.frmStudentMaintenance.get("yearGroup").setValue(student.studentPupilDetailsItem.yearGroup);
          this.frmStudentMaintenance.get("mainSubject").setValue(student.studentPupilDetailsItem.mainSubject);
          this.frmStudentMaintenance.get("pupilPremium").setValue(student.studentPupilDetailsItem.eligableForPupilPremium);
          this.frmStudentMaintenance.get("sendPupil").setValue(student.studentPupilDetailsItem.sendPupil);
          this.frmStudentMaintenance.get("client").setValue(student.studentPupilDetailsItem.clientId);
          this.clientId = `${student.studentPupilDetailsItem.clientId}`
          let cin = student.studentPupilDetailsItem.cin == null ? false : student.studentPupilDetailsItem.cin;
          let lac = student.studentPupilDetailsItem.lac == null ? false : student.studentPupilDetailsItem.lac;
          this.frmStudentMaintenance.get("childrenInNeed").setValue(cin);
          this.frmStudentMaintenance.get("lookingAfterChildren").setValue(lac);

          if (student.studentPupilDetailsItem.pupilDataSharingWithdrawn == null || !student.studentPupilDetailsItem.pupilDataSharingWithdrawn) {
            this.frmStudentMaintenance.get("withdrawSharingData").setValue(false);
          } else {
            this.frmStudentMaintenance.get("withdrawSharingData").setValue(true);
          }
        }

        if (student.studentDetailItem != null) {
          this.studentDisplayName = student.studentDetailItem.firstName + ' ' + student.studentDetailItem.surname;
          this.frmStudentMaintenance.get("firstName").setValue(student.studentDetailItem.firstName);
          this.frmStudentMaintenance.get("lastName").setValue(student.studentDetailItem.surname);
          this.frmStudentMaintenance.get("dateOfBirth").setValue(student.studentDetailItem.dateOfBirth);
          this.frmStudentMaintenance.get("gender").setValue(student.studentDetailItem.gender);
          this.frmStudentMaintenance.get("emailAddress").setValue(student.studentDetailItem.emailAddress);
          this.frmStudentMaintenance.get("hiddenFromAllocation").setValue(student.studentDetailItem.hiddenFromAllocation ? student.studentDetailItem.hiddenFromAllocation : false);
          // this.frmStudentMaintenance.get("age").setValue(student.studentDetailItem.age);

          if (student.studentDetailItem.dateOfBirth != null) {
            let timeDiff = Math.abs(Date.now() - new Date(student.studentDetailItem.dateOfBirth).getTime());
            let age = Math.floor((timeDiff / (1000 * 3600 * 24)) / 365.25);
            this.frmStudentMaintenance.get("age").setValue(age);
          }

          this.studentCreatedOn = student.studentDetailItem.createdOn;
          this.studentUpdatedOn = student.studentDetailItem.updatedOn;
          this.studentQuickInfo = student.studentDetailItem.studentQuickLabel;
        }

        if (student.studentAdditionalDetailsItem != null) {
          this.frmStudentMaintenance.get("learnerBackground").setValue(student.studentAdditionalDetailsItem.learnerBackground);
          this.frmStudentMaintenance.get("behaviorBackground").setValue(student.studentAdditionalDetailsItem.behaviourBackground);
          this.frmStudentMaintenance.get("healthAndSafetyInfo").setValue(student.studentAdditionalDetailsItem.healthAndSafety);
          this.frmStudentMaintenance.get("additionalNotes").setValue(student.studentAdditionalDetailsItem.studentNotes);
        }

        this.frmStudentMaintenance.disable();

        this.modalInitializing = false;
        this.cd.detectChanges();
      } else {
        this.modalInitializing = false;
        this.cd.detectChanges();
        console.error("No student found for Id");
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", { isValidationError: false });
      }

    }, error => {
      this.modalInitializing = false;
      this.cd.detectChanges();
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  private handleInsertUpdateResponse(response: StudentMergeResponse, methodName: string) {

    if (response.successful) {
      this.studentId = response.studentId;
      this.showEditButton = true;
      this.successModalHelper.show("The save was successful");
    }

    this.successfulUpload = response.successful;
    this.uploadResponseMainMessage = response.reponseMessage;
    this.uploadResponseMessages = response.reponseMessages == null ? new Array<string>() : response.reponseMessages;

  }


  public launchVideoLibrary() {

    const request: LaunchVideoLibrary =
    {
      digitalClassroomId: this.placement.digitalClassroomId,
      isSuperUser: false,
      clientId: parseInt(this.clientId, 10),
      tutorId: 0,
      isStudent: false
    };

    this.placementService.launchVideoLibrary(request).subscribe((access: DigitalClassroomAccess) => {
      setTimeout(() => {
        window.open(`${access.accessToken}&userref=${this.studentId}STU`, '_blank');
      }, 0);

    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);

    });
  }

  private getPlacementInformation() {
    try {
      this.studentService.readPlacementInformation(this.studentId).subscribe((data: Array<PlacementInformation>) => {
        this.placementInformation = data;
        this.placement = this.placementInformation.find(x => x.digitalClassroomEnabled);

        if (this.placement) {
          this.showLaunchVideoLibrary = this.placement.digitalClassroomEnabled;
        }
        else {
          this.showLaunchVideoLibrary = false;
        }
      })

    } catch (error) {

      let errorMessage = error.status === 401 ? "Unauthorized" : error.message;

      if (errorMessage == "Object reference not set to an instance of an object.") {
        errorMessage = "Client not found. Try another."
      }

      this.errorModalHelper.show(errorMessage, error);
    }

  }

  // showTutorReport()console.log('tutorReportsHelperService', this.tutorReportsHelperService.commonData);
  public showTutorReport(item: PlacementInformation) {

    console.log('item', item);
    this.dialogRef.close();

    this.tutorReportsHelperService.setCommonDataBeforeNavigate(+item.placementId, item.startDate, item.endDate);
    setTimeout(() => {
      this.router.navigate(["/tutorreports", +item.placementId]);
    }, 200);
  }

  public showMessage() {
    this.renderer.setStyle(this.message.nativeElement, 'opacity', 1);
  }
  public hideMessage() {
    this.renderer.setStyle(this.message.nativeElement, 'opacity', 0);
  }
}
